import { Component, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ApiService, AoiBounds, AreaVesselCandidate, BillOfLadingCountry, BillOfLadingLookupData, BillOfLadingRoute, BillOfLadingTrackedRecord, TrackedVesselRecord, VesselAisHistoryData, VesselAisHistoryResponse, VesselLiveObservation, VesselSearchCandidate } from 'src/app/core/services/api.service';
import { CommonModule, DatePipe } from '@angular/common';
import { EMPTY, Observable, Subject, Subscription, forkJoin, of, timer } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { SubmitParams } from 'src/app/core/models/params';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmReportComponent } from 'src/app/shared/components/confirm-report/confirm-report.component';
import { NavbarComponent } from 'src/app/shared/components/navbar/navbar.component';
import { MyAuthService } from 'src/app/core/services/auth.service';
import { AuthService } from '@auth0/auth0-angular';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FooterComponent } from "../../shared/components/footer/footer.component";
import { SearchComponent } from 'src/app/shared/components/search/search.component';
import { StepIndicatorComponent } from 'src/app/shared/components/step-indicator/step-indicator.component';
import { PackageSelectionComponent } from 'src/app/shared/components/package-selection/package-selection.component';
import { Package } from 'src/app/core/models/package';
import { packages as fallbackPackages } from 'src/app/core/models/packages';
import { catchError, expand, finalize, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatButtonModule } from '@angular/material/button';
import { SearchFormComponent } from '../search-form/search-form.component';
import { NavigationRailComponent } from '../../shared/components/navigation-rail/navigation-rail.component';
import { VesselMapComponent } from '../../shared/components/vessel-map/vessel-map.component';
import { PersistentAisHistoryOverlay, PersistentBolRouteOverlay, VesselMapPosition } from '../../shared/components/vessel-map/vessel-map.models';
import { MaritimeRequestDialogComponent } from '../../shared/components/maritime-request-dialog/maritime-request-dialog.component';
import { SELECTED_VESSEL_BLOCKS, SelectedVesselBlockName, SelectedVesselDetailsResponse } from 'src/app/core/models/selected-vessel-data.model';
import { TutorialAnchorDirective } from 'src/app/core/tutorial/tutorial-anchor.directive';
import { SubscriptionCheckoutComponent } from 'src/app/shared/components/subscription-checkout/subscription-checkout.component';
import { SubscriptionEntitlementService } from 'src/app/core/services/subscription-entitlement.service';
import { TutorialService } from 'src/app/core/tutorial/tutorial.service';

interface TrackedVessel {
  callsign: string;
  destination: string;
  draught: string;
  flag: string;
  mmsi: string;
  name: string;
  navigationStatus: string;
  imo: string;
  type: string;
  position?: VesselMapPosition;
  speed: number | null;
  course: number | null;
  etaUtc: string | null;
  lastPositionUtc: string | null;
}

export interface TrackedVesselCase {
  imo: string;
  name: string;
  reportType: string;
  createdAt: string | null;
}

interface TutorialDemoSnapshot {
  activeVessel: TrackedVessel;
  selectedVesselDetails?: SelectedVesselDetailsResponse['data'];
  selectedVesselDetailsLoading: boolean;
  selectedVesselDetailsError: string;
  weatherLocation?: { latitude: number; longitude: number; label: string };
  mapPositions: VesselMapPosition[];
  vesselResolveLoading: boolean;
  vesselResolveError: string;
  selectedAreaVessel?: AreaVesselCandidate;
  areaResultsVisible: boolean;
  activeVesselTab: 'tracked' | 'area';
}

const TOUR_DEMO_POSITION: VesselMapPosition = {
  longitude: 14.2681,
  latitude: 40.8518,
  heading: 136,
  name: 'DEMO VOYAGER',
  imo: 'IMO 9312345'
};

const TOUR_DEMO_DETAILS: SelectedVesselDetailsResponse['data'] = {
  summary: {
    imo: '9312345', mmsi: '247123456', uuid: null, name: 'DEMO VOYAGER', aisName: 'DEMO VOYAGER',
    callsign: 'IBDV', countryIso: 'IT', countryName: 'Italy', type: 'Cargo', typeSpecific: 'Container Ship',
    latitude: TOUR_DEMO_POSITION.latitude, longitude: TOUR_DEMO_POSITION.longitude, speed: 13.2, course: 136,
    heading: 136, navigationStatus: 'Under way using engine', originalAisDestination: 'Genoa',
    etaUtc: '2026-10-16T08:00:00Z', lastPositionUtc: '2026-10-15T12:00:00Z', currentDraught: 8.4,
    departurePort: { uuid: null, unlocode: 'ITNAP', name: 'Naples' },
    destinationPort: { uuid: null, unlocode: 'ITGOA', name: 'Genoa' },
    atdUtc: '2026-10-15T06:00:00Z', timezone: 'Europe/Rome', timezoneOffsetSeconds: 7200
  },
  blocks: {
    ports: { status: 'available', data: [] },
    history: { status: 'available', data: [] },
    route: { status: 'available', data: { distanceNm: 318, coordinates: [[14.2681, 40.8518], [8.9463, 44.4056]] } }
  }
};

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    providers: [ApiService, DatePipe],
    animations: [
        trigger('stepAnimation', [
            state('search', style({ opacity: 1, transform: 'translateY(0)' })),
            state('packages', style({ opacity: 1, transform: 'translateY(0)' })),
            state('checkout', style({ opacity: 1, transform: 'translateY(0)' })),
            transition('* => *', [
                style({ opacity: 0, transform: 'translateY(-20px)' }),
                animate('500ms ease-in-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ])
        ])
    ],
    imports: [
        CommonModule,
        NavbarComponent,
        MatIconModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatDialogModule,
        FooterComponent,
        SearchComponent,
        SearchFormComponent,
        StepIndicatorComponent,
        PackageSelectionComponent,
        ConfirmReportComponent,
        MatCheckboxModule,
        MatButtonModule,
        NavigationRailComponent,
        VesselMapComponent,
        TutorialAnchorDirective
    ]
})
export class HomeComponent implements OnInit, OnDestroy {
    @ViewChild('vesselMap') vesselMap?: VesselMapComponent;
  apiService = inject(ApiService);
  dialog = inject(MatDialog);
  myAuthService = inject(MyAuthService)
  auth = inject(AuthService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  subscriptionEntitlement = inject(SubscriptionEntitlementService);
  tutorial = inject(TutorialService);
  
  private destroy$ = new Subject<void>();
  private subscriptionPromptShown = false;
  
  // Multi-step form state
  currentStep: 'search' | 'packages' | 'checkout' = 'search';
  selectedEntities: {
    companies: any[];
    individuals: any[];
    vessels: any[];
  } = { companies: [], individuals: [], vessels: [] };
  selectedPackages: Package[] = [];
  uboSelected: boolean = false;
  packages: Package[] = fallbackPackages;
  catalogLoading = false;
  catalogError = '';
  unresolvedVessel: { name: string; imo: string | null } | null = null;
  unresolvedVesselContext: Partial<VesselSearchCandidate> & { query?: string } = {};
  
  // Search form state
  showSearchForm: boolean = false;
  initialSearchFormType: 'Companies' | 'Individuals' | 'Vessels' = 'Companies';
  
  // Search results state
  searchResults: {
    companies?: any[];
    individuals?: any[];
    vessels?: any[];
  } = {};
  
  currentEntityType: 'company' | 'individual' | 'vessel' | null = null;
  trackedVessels: TrackedVesselCase[] = [];
  acquiredProducts: TrackedVesselCase[] = [];
  savedVessels: TrackedVesselRecord[] = [];
  trackedVesselsLoading = false;
  trackedVesselsError = '';
  trackingInProgress = false;
  trackingMessage = '';
  activeVessel: TrackedVessel = this.createEmptyTrackedVessel();
  selectedVesselDetails?: SelectedVesselDetailsResponse['data'];
  selectedVesselDetailsLoading = false;
  selectedVesselDetailsError = '';
  weatherLocation?: { latitude: number; longitude: number; label: string };
  retryingDetailBlocks = new Set<SelectedVesselBlockName>();
  private detailsRequestToken = 0;
  mapPositions: VesselMapPosition[] = [];
  selectedReport = 'Vessel UCB';
  directProductSelection = false;
  manualPaidRequest = false;
  vesselResolveLoading = false;
  vesselResolveError = '';
  areaBounds?: AoiBounds;
  areaResults: AreaVesselCandidate[] = [];
  areaResultsVisible = false;
  selectedAreaVessel?: AreaVesselCandidate;
  areaSearchLoading = false;
  areaSearchError = '';
  areaRequestMessage = '';
  activeVesselTab: 'tracked' | 'area' = 'tracked';
  bolLookup?: BillOfLadingLookupData;
  billOfLadingLockedPlaceholders: number[] = [];
  trackedBols: BillOfLadingTrackedRecord[] = [];
  trackedBolsLoading = false;
  trackedBolsError = '';
  bolRoute?: BillOfLadingRoute;
  bolRouteLoading = false;
  bolRouteError = '';
  bolRouteVisible = false;
  bolRoutePinned = false;
  datasetRequestLoading = false;
  datasetRequestMessage = '';
  private tutorialDemoSnapshot?: TutorialDemoSnapshot;
  persistentHistoryOverlays: PersistentAisHistoryOverlay[] = [];
  persistentBolRoutes: PersistentBolRouteOverlay[] = [];
  readonly historyLoadingImos = new Set<string>();
  readonly historyCompletedImos = new Set<string>();
  readonly historyErrors = new Map<string, string>();
  private readonly historyRequests = new Map<string, Subscription>();
  private restoredVesselNavigationKey = '';

  // Legacy properties
  params?: SubmitParams
  isAdmin$: Observable<boolean> = this.myAuthService.isAdmin();
  user$?: Observable<any> = this.auth.user$;

  ngOnInit(): void {
    this.offerSubscriptionCheckout();
    this.loadTrackedVessels();
    this.loadTrackedBols();
    this.loadProductCatalog();
    this.tutorial.active$.pipe(takeUntil(this.destroy$)).subscribe(active => {
      if (active) this.enterTutorialDemo();
      else this.exitTutorialDemo();
    });
    
    // Listen to query parameter changes
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.restoreVesselFromNavigation(params);
      if (params['intent'] === 'vessel-search') {
        this.openVesselSearch();
        return;
      }

      const step = params['step'] || 'search';
      if (this.isValidStep(step)) {
        this.currentStep = step;
        this.validateStepAccess();
      } else {
        this.navigateToStep('search', true);
      }
    });
  }

  private restoreVesselFromNavigation(params: Params): void {
    const candidateFromState = (window.history.state?.vesselCandidate ?? null) as VesselSearchCandidate | null;
    const imo = params['vesselImo'] || candidateFromState?.imo || '';
    const name = params['vesselName'] || candidateFromState?.name || '';
    if (!imo && !name) return;
    const key = `${imo}|${name}`;
    if (this.restoredVesselNavigationKey === key) return;
    this.restoredVesselNavigationKey = key;

    if (candidateFromState) {
      this.onVesselSearchSelection(candidateFromState);
      return;
    }

    this.apiService.searchVessels(imo || name).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        const candidate = (response.data || []).find(item => (imo && item.imo === imo) || (!imo && item.name === name));
        if (candidate) this.onVesselSearchSelection(candidate);
      },
      error: () => undefined
    });
  }

  private offerSubscriptionCheckout(): void {
    this.subscriptionEntitlement.canAccessWorkspace().pipe(takeUntil(this.destroy$)).subscribe({
      next: access => {
        if (access || this.subscriptionPromptShown) return;
        this.subscriptionPromptShown = true;
        this.dialog.open(SubscriptionCheckoutComponent, {
          width: 'min(1010px, calc(100vw - 32px))',
          maxWidth: '1010px',
          autoFocus: false,
          disableClose: true,
          data: { requiredActivation: true },
          panelClass: 'tevuna-subscription-checkout-modal',
          backdropClass: 'tevuna-subscription-checkout-backdrop'
        });
      },
      error: () => undefined
    });
  }

  ngOnDestroy(): void {
    this.exitTutorialDemo();
    this.destroy$.next();
    this.destroy$.complete();
    this.historyRequests.forEach(request => request.unsubscribe());
    this.historyRequests.clear();
  }

  private enterTutorialDemo(): void {
    if (this.tutorialDemoSnapshot) return;
    this.tutorialDemoSnapshot = {
      activeVessel: this.activeVessel,
      selectedVesselDetails: this.selectedVesselDetails,
      selectedVesselDetailsLoading: this.selectedVesselDetailsLoading,
      selectedVesselDetailsError: this.selectedVesselDetailsError,
      weatherLocation: this.weatherLocation,
      mapPositions: this.mapPositions,
      vesselResolveLoading: this.vesselResolveLoading,
      vesselResolveError: this.vesselResolveError,
      selectedAreaVessel: this.selectedAreaVessel,
      areaResultsVisible: this.areaResultsVisible,
      activeVesselTab: this.activeVesselTab
    };
    this.detailsRequestToken++;
    this.activeVessel = {
      callsign: 'IBDV', destination: 'Genoa', draught: '8.4 m', flag: 'Italy', mmsi: '247123456',
      name: 'DEMO VOYAGER', navigationStatus: 'Under way using engine', imo: 'IMO 9312345',
      type: 'Container Ship', position: TOUR_DEMO_POSITION, speed: 13.2, course: 136,
      etaUtc: '2026-10-16T08:00:00Z', lastPositionUtc: '2026-10-15T12:00:00Z'
    };
    this.selectedVesselDetails = TOUR_DEMO_DETAILS;
    this.selectedVesselDetailsLoading = false;
    this.selectedVesselDetailsError = '';
    this.weatherLocation = { latitude: TOUR_DEMO_POSITION.latitude, longitude: TOUR_DEMO_POSITION.longitude, label: 'DEMO VOYAGER' };
    this.mapPositions = [TOUR_DEMO_POSITION];
    this.vesselResolveLoading = false;
    this.vesselResolveError = '';
    this.selectedAreaVessel = undefined;
    this.areaResultsVisible = false;
    this.activeVesselTab = 'tracked';
  }

  private exitTutorialDemo(): void {
    if (!this.tutorialDemoSnapshot) return;
    const snapshot = this.tutorialDemoSnapshot;
    this.tutorialDemoSnapshot = undefined;
    this.activeVessel = snapshot.activeVessel;
    this.selectedVesselDetails = snapshot.selectedVesselDetails;
    this.selectedVesselDetailsLoading = snapshot.selectedVesselDetailsLoading;
    this.selectedVesselDetailsError = snapshot.selectedVesselDetailsError;
    this.weatherLocation = snapshot.weatherLocation;
    this.mapPositions = snapshot.mapPositions;
    this.vesselResolveLoading = snapshot.vesselResolveLoading;
    this.vesselResolveError = snapshot.vesselResolveError;
    this.selectedAreaVessel = snapshot.selectedAreaVessel;
    this.areaResultsVisible = snapshot.areaResultsVisible;
    this.activeVesselTab = snapshot.activeVesselTab;
  }

  loadTrackedVessels(): void {
    this.trackedVesselsLoading = true;
    this.trackedVesselsError = '';
    const savedVessels$ = typeof (this.apiService as any).getTrackedVessels === 'function'
      ? this.apiService.getTrackedVessels().pipe(catchError(() => of({ data: [] as TrackedVesselRecord[] })))
      : of({ data: [] as TrackedVesselRecord[] });
    forkJoin({ reports: this.apiService.getDiligencesByEmail(), saved: savedVessels$ }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ reports, saved }) => {
        this.savedVessels = saved.data ?? [];
        this.trackedVessels = this.normalizeTrackedVesselCases(undefined, this.savedVessels);
        this.acquiredProducts = this.normalizeTrackedVesselCases(reports);
        this.trackedVesselsLoading = false;
      },
      error: () => {
        this.trackedVessels = [];
        this.acquiredProducts = [];
        this.trackedVesselsError = 'Tracked vessel history is temporarily unavailable.';
        this.trackedVesselsLoading = false;
      }
    });
  }

  normalizeTrackedVesselCases(payload: any, savedVessels: TrackedVesselRecord[] = []): TrackedVesselCase[] {
    const ordinary = Array.isArray(payload) ? payload : (payload?.dueDiligences ?? payload?.data ?? []);
    const manual = Array.isArray(payload?.manualReports) ? payload.manualReports : [];
    const reportCases = [...ordinary, ...manual].map(report => {
      const rawImo = report?.imo ?? report?.imoNumber;
      const imo = String(rawImo ?? '').replace(/^IMO\s*/i, '').replace(/\s+/g, '');
      const type = report?.isManual || report?.vesselName ? 'VESSEL' : String(report?.type ?? report?.entityType ?? '').toUpperCase();
      const name = String(report?.name ?? report?.target ?? report?.vesselName ?? '').trim();
      const createdAt = report?.requestedAt ?? report?.created_at ?? report?.createdAt ?? report?.updated_at ?? report?.updatedAt ?? null;
      const reportCode = report?.report ?? report?.product ?? report?.package ?? '';
      const packageName = (this.packages ?? fallbackPackages).find(item => item.code === reportCode)?.name;
      return { imo, name, type, createdAt, reportType: packageName ?? String(reportCode || 'Report') };
    }).filter(item => item.type === 'VESSEL' && /^\d{7}$/.test(item.imo) && Boolean(item.name));

    const savedCases = savedVessels.map(vessel => ({
      imo: String(vessel.imo ?? '').replace(/^IMO\s*/i, '').replace(/\s+/g, ''),
      name: String(vessel.name ?? '').trim(), reportType: 'Tracked vessel', createdAt: vessel.trackedAt ?? null
    })).filter(item => /^\d{7}$/.test(item.imo) && Boolean(item.name));
    const sorted = [...reportCases, ...savedCases].sort((a, b) => {
      const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
      const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
      return bTime - aTime;
    });
    const byImo = new Map<string, typeof sorted[number]>();
    sorted.forEach(item => { if (!byImo.has(item.imo)) byImo.set(item.imo, item); });
    return Array.from(byImo.values()).slice(0, 10).map(({ imo, name, reportType, createdAt }) => ({ imo, name, reportType, createdAt }));
  }

  get canTrackSelectedVessel(): boolean { return /^\d{7}$/.test(this.activeVessel.imo.replace(/^IMO\s*/i, '')); }

  get selectedVesselIsTracked(): boolean {
    const imo = this.activeVessel.imo.replace(/^IMO\s*/i, '');
    return this.trackedVessels.some(vessel => vessel.imo === imo);
  }

  trackSelectedVessel(): void {
    if (!this.canTrackSelectedVessel || this.selectedVesselIsTracked || this.trackingInProgress) return;
    this.trackingInProgress = true;
    this.trackingMessage = '';
    this.apiService.trackVessel({ imo: this.activeVessel.imo.replace(/^IMO\s*/i, ''), name: this.activeVessel.name }).pipe(
      takeUntil(this.destroy$), finalize(() => this.trackingInProgress = false)
    ).subscribe({
      next: () => { this.trackingMessage = 'Vessel added to Tracked vessels.'; this.loadTrackedVessels(); },
      error: () => { this.trackingMessage = 'Unable to track this vessel. Please try again.'; }
    });
  }

  removeTrackedVessel(vessel: TrackedVesselCase, event: Event): void {
    event.stopPropagation();
    this.apiService.deleteTrackedVessel(vessel.imo).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.historyRequests.get(vessel.imo)?.unsubscribe();
        this.historyRequests.delete(vessel.imo);
        this.historyLoadingImos.delete(vessel.imo);
        this.historyCompletedImos.delete(vessel.imo);
        this.historyErrors.delete(vessel.imo);
        this.persistentHistoryOverlays = this.persistentHistoryOverlays.filter(overlay => overlay.imo !== vessel.imo);
        this.trackedVessels = this.trackedVessels.filter(item => item.imo !== vessel.imo);
        this.savedVessels = this.savedVessels.filter(item => item.imo !== vessel.imo);
      },
      error: () => { this.trackedVesselsError = 'The tracked vessel could not be removed.'; }
    });
  }

  selectTrackedVessel(vessel: TrackedVesselCase): void {
    this.selectedAreaVessel = undefined;
    this.areaResultsVisible = false;
    this.activeVesselTab = 'tracked';
    this.activeVessel = this.trackedCaseToVessel(vessel);
    this.resetSelectedVesselDetails();
    this.vesselResolveError = '';
    this.vesselResolveLoading = true;
    this.apiService.getLiveVessel(vessel.imo).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.activeVessel = this.toTrackedVessel({ imo: vessel.imo, name: vessel.name, mmsi: null, uuid: null, type: 'Vessel', countryIso: null, yearBuilt: null }, response.data);
        if (this.selectedVesselDetails?.summary) this.applySelectedVesselSummary(this.selectedVesselDetails.summary);
        this.mapPositions = this.removeMapPosition(vessel.imo);
        if (this.activeVessel.position) {
          this.mapPositions = [...this.mapPositions, this.activeVessel.position];
        }
        this.loadSelectedVesselDetails(vessel.imo);
        this.vesselResolveLoading = false;
      },
      error: () => {
        this.loadSelectedVesselDetails(vessel.imo);
        this.vesselResolveLoading = false;
        this.vesselResolveError = 'Live vessel data is temporarily unavailable.';
      }
    });
  }

  isHistoryShown(imo: string): boolean {
    return this.persistentHistoryOverlays.some(overlay => overlay.imo === imo);
  }

  historyActionLabel(vessel: TrackedVesselCase): string {
    return this.isHistoryShown(vessel.imo) ? 'Hide Historical AIS' : 'Show Historical AIS';
  }

  toggleTrackedVesselHistory(vessel: TrackedVesselCase, event: Event): void {
    event.stopPropagation();
    if (this.isHistoryShown(vessel.imo)) {
      this.historyRequests.get(vessel.imo)?.unsubscribe();
      this.historyRequests.delete(vessel.imo);
      this.historyLoadingImos.delete(vessel.imo);
      this.historyCompletedImos.delete(vessel.imo);
      this.persistentHistoryOverlays = this.persistentHistoryOverlays.filter(overlay => overlay.imo !== vessel.imo);
      this.historyErrors.delete(vessel.imo);
      return;
    }
    if (this.historyLoadingImos.has(vessel.imo)) return;

    this.historyErrors.delete(vessel.imo);
    this.historyCompletedImos.delete(vessel.imo);
    this.historyLoadingImos.add(vessel.imo);
    const request = this.apiService.startVesselAisHistory(vessel.imo).pipe(
      expand(response => this.isPendingHistory(response)
        ? timer(1500).pipe(switchMap(() => this.apiService.getVesselAisHistory(vessel.imo, response.request_id!)))
        : EMPTY),
      takeWhile(response => this.isPendingHistory(response), true),
      takeUntil(this.destroy$),
      finalize(() => {
        this.historyLoadingImos.delete(vessel.imo);
        this.historyRequests.delete(vessel.imo);
      })
    ).subscribe({
      next: response => this.applyAisHistoryResponse(vessel, response),
      error: error => {
        this.historyCompletedImos.delete(vessel.imo);
        this.historyErrors.set(vessel.imo, this.historyErrorMessage(error));
      }
    });
    this.historyRequests.set(vessel.imo, request);
  }

  historyErrorFor(imo: string): string | undefined { return this.historyErrors.get(imo); }

  isHistoryComplete(imo: string): boolean { return this.historyCompletedImos.has(imo); }

  private isPendingHistory(response: VesselAisHistoryResponse): boolean {
    return (response.status === 'queued' || response.status === 'running') && Boolean(response.request_id);
  }

  private applyAisHistoryResponse(vessel: TrackedVesselCase, response: VesselAisHistoryResponse): void {
    const snapshot = response.data ?? response.partial;
    if (snapshot) {
      const overlay = this.toPersistentHistoryOverlay(vessel, snapshot);
      if (!overlay.tracks.length) {
        if (response.status === 'complete') this.historyErrors.set(vessel.imo, 'No usable AIS positions are available for this vessel.');
        return;
      }
      this.persistentHistoryOverlays = [...this.persistentHistoryOverlays.filter(item => item.imo !== vessel.imo), overlay];
      this.historyErrors.delete(vessel.imo);
      if (response.status === 'complete') this.historyCompletedImos.add(vessel.imo);
      else this.historyCompletedImos.delete(vessel.imo);
    }
    if (response.status === 'failed') {
      this.historyCompletedImos.delete(vessel.imo);
      this.historyErrors.set(vessel.imo, this.historyFailureMessage(response.error));
    }
  }

  private toPersistentHistoryOverlay(vessel: TrackedVesselCase, data: VesselAisHistoryData): PersistentAisHistoryOverlay {
    const colors = ['#4fe3d1', '#52b7ff', '#d7a9ff', '#f2c96d', '#ff91ad', '#8de99d'];
    const colorIndex = Array.from(vessel.imo).reduce((sum, digit) => sum + Number(digit), 0) % colors.length;
    return {
      imo: vessel.imo,
      name: data.name || vessel.name,
      color: colors[colorIndex],
      tracks: (data.tracks ?? []).map(track => ({
        id: track.id,
        positions: (track.positions ?? []).filter(position => Number.isFinite(position.latitude) && Number.isFinite(position.longitude)
          && Math.abs(position.latitude) <= 90 && Math.abs(position.longitude) <= 180).map(position => ({
          latitude: position.latitude, longitude: position.longitude, observedAt: position.observed_at
        }))
      })).filter(track => track.positions.length > 1)
    };
  }

  private historyErrorMessage(error: any): string {
    if (error?.status === 422) return 'No usable full AIS history is available for this vessel.';
    if ([429, 502, 503, 504].includes(error?.status)) return 'AIS history is temporarily unavailable. Please retry.';
    return 'AIS history could not be retrieved. Please try again.';
  }

  private historyFailureMessage(error: unknown): string {
    if (typeof error === 'string' && error.trim()) return error;
    if (error && typeof error === 'object') {
      const payload = error as Record<string, unknown>;
      for (const key of ['message', 'error', 'detail']) {
        const value = payload[key];
        if (typeof value === 'string' && value.trim()) return value;
      }
    }
    return 'AIS history could not be retrieved. Please try again.';
  }

  onAreaSelected(bounds: AoiBounds): void {
    this.areaBounds = bounds;
    this.areaSearchError = '';
    this.areaRequestMessage = '';
  }

  onDarkShipAreaSelected(bounds: AoiBounds): void { this.openDarkShipRequest(bounds); }

  searchArea(bounds: AoiBounds): void {
    this.areaBounds = bounds;
    this.areaSearchLoading = true;
    this.areaSearchError = '';
    this.areaResultsVisible = false;
    this.activeVesselTab = 'area';
    this.selectedAreaVessel = undefined;
    this.apiService.searchVesselsInArea(bounds).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.areaResults = response.data ?? [];
        this.areaResultsVisible = true;
        this.activeVesselTab = 'area';
        this.areaSearchLoading = false;
        this.mapPositions = this.areaResults
          .filter(vessel => Number.isFinite(vessel.latitude) && Number.isFinite(vessel.longitude))
          .map(vessel => this.areaCandidateToPosition(vessel));
      },
      error: error => {
        this.areaSearchLoading = false;
        this.areaResultsVisible = true;
        this.areaSearchError = [429, 502, 503].includes(error?.status)
          ? 'Area search is temporarily unavailable. Try again.'
          : 'The selected area could not be searched.';
      }
    });
  }

  selectAreaVessel(vessel: AreaVesselCandidate): void {
    this.selectedAreaVessel = vessel;
    this.activeVessel = this.areaCandidateToTrackedVessel(vessel);
    this.resetSelectedVesselDetails();
    if (vessel.imo) this.loadSelectedVesselDetails(vessel.imo);
    this.mapPositions = this.areaResults
      .filter(candidate => Number.isFinite(candidate.latitude) && Number.isFinite(candidate.longitude))
      .map(candidate => this.areaCandidateToPosition(candidate));
  }

  onMapVesselSelected(imo: string): void {
    const normalizedImo = this.normalizeVesselImo(imo);
    if (!normalizedImo) return;

    if (this.areaResultsVisible) {
      const areaVessel = (this.areaResults ?? []).find(candidate => this.normalizeVesselImo(candidate.imo) === normalizedImo);
      if (areaVessel) {
        this.vesselMap?.preserveViewportForVessel(areaVessel.imo);
        this.selectAreaVessel(areaVessel);
      }
      return;
    }

    const trackedVessel = [...(this.trackedVessels ?? []), ...(this.acquiredProducts ?? [])]
      .find(candidate => this.normalizeVesselImo(candidate.imo) === normalizedImo);
    if (trackedVessel) {
      this.vesselMap?.preserveViewportForVessel(trackedVessel.imo);
      this.selectTrackedVessel(trackedVessel);
    }
  }

  private normalizeVesselImo(imo: string | null | undefined): string {
    return String(imo ?? '').replace(/\D/g, '');
  }

  backToAreaResults(): void {
    this.selectedAreaVessel = undefined;
    this.mapPositions = this.areaResults.filter(vessel => Number.isFinite(vessel.latitude) && Number.isFinite(vessel.longitude)).map(vessel => this.areaCandidateToPosition(vessel));
  }

  openDarkShipRequest(bounds: AoiBounds): void {
    const ref = this.dialog.open(MaritimeRequestDialogComponent, {
      data: { kind: 'dark_ship', aoi: bounds },
      panelClass: 'tevuna-maritime-request',
      backdropClass: 'tevuna-maritime-request-backdrop'
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) this.areaRequestMessage = 'Dark Ship request accepted.';
      else this.vesselMap?.resetAreaSelection();
    });
  }

  openProductRequest(query = '', requestedProducts?: Package[], vesselContext?: Partial<VesselSearchCandidate>): void {
    const open = (products: Package[]) => {
        const hasImo = /^\d{7}$/.test(this.activeVessel.imo.replace(/^IMO\s*/i, ''));
        const manualProducts = (requestedProducts ?? products).filter(product => (product.manual || (!hasImo && product.code === 'R8')) && product.available !== false && product.supportedEntityTypes?.includes('VESSEL'));
        if (!manualProducts.length) { this.catalogError = 'No manual vessel products are currently available.'; return; }
        const ref = this.dialog.open(MaritimeRequestDialogComponent, {
          data: { kind: 'product_request', products: manualProducts, query, vesselContext: vesselContext ?? this.unresolvedVesselContext },
          panelClass: 'tevuna-maritime-request',
          backdropClass: 'tevuna-maritime-request-backdrop'
        });
        ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
          if (result) this.areaRequestMessage = 'Product request accepted.';
        });
    };
    if (requestedProducts) { open(requestedProducts); return; }
    if (this.packages.length) { open(this.packages); return; }
    this.apiService.getPackages().pipe(takeUntil(this.destroy$)).subscribe({ next: products => { this.packages = products; open(products); }, error: () => { this.areaRequestMessage = 'Product catalog is temporarily unavailable.'; } });
  }

  private areaCandidateToPosition(vessel: AreaVesselCandidate): VesselMapPosition {
    return { longitude: vessel.longitude as number, latitude: vessel.latitude as number, heading: vessel.heading ?? undefined, name: vessel.name || 'Unnamed vessel', imo: vessel.imo ? `IMO ${vessel.imo}` : '—' };
  }

  private areaCandidateToTrackedVessel(vessel: AreaVesselCandidate): TrackedVessel {
    const position = Number.isFinite(vessel.latitude) && Number.isFinite(vessel.longitude) ? this.areaCandidateToPosition(vessel) : undefined;
    return { callsign: '—', destination: vessel.destination ?? '—', draught: '—', flag: vessel.countryIso ?? '—', mmsi: vessel.mmsi ?? '—', name: vessel.name ?? 'Unnamed vessel', navigationStatus: '—', imo: vessel.imo ? `IMO ${vessel.imo}` : '—', type: vessel.type ?? '—', position, speed: vessel.speed, course: vessel.course, etaUtc: vessel.etaUtc, lastPositionUtc: vessel.lastPositionUtc };
  }

  onVesselSearchSelection(candidate: VesselSearchCandidate): void {
    this.bolLookup = undefined;
    this.selectedAreaVessel = undefined;
    this.resetSelectedVesselDetails();
    if (!candidate.imo) {
      this.unresolvedVesselContext = { ...candidate, query: candidate.name ?? undefined };
      this.activeVessel = this.toTrackedVessel(candidate, {
        imo: null,
        mmsi: candidate.mmsi,
        uuid: candidate.uuid,
        name: candidate.name,
        latitude: candidate.latitude ?? null,
        longitude: candidate.longitude ?? null,
        speed: candidate.speed ?? null,
        course: candidate.course ?? null,
        heading: candidate.heading ?? null,
        navigationStatus: null,
        destination: candidate.destination ?? null,
        etaUtc: candidate.etaUtc ?? null,
        lastPositionUtc: candidate.lastPositionUtc ?? null,
        source: 'datalastic-search'
      });
      this.mapPositions = this.mergeMapPosition(this.activeVessel.position);
      this.vesselResolveError = 'AIS unavailable. You can still request a product.';
      return;
    }

    this.vesselResolveLoading = true;
    this.vesselResolveError = '';
    this.apiService.getLiveVessel(candidate.imo).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => {
        this.activeVessel = this.toTrackedVessel(candidate, response.data);
        if (this.selectedVesselDetails?.summary) this.applySelectedVesselSummary(this.selectedVesselDetails.summary);
        this.mapPositions = this.mergeMapPosition(this.activeVessel.position);
        this.loadSelectedVesselDetails(candidate.imo as string);
        this.vesselResolveLoading = false;
      },
      error: () => {
        this.vesselResolveLoading = false;
        this.activeVessel = this.toTrackedVessel(candidate, { ...candidate, imo: candidate.imo, name: candidate.name, latitude: null, longitude: null, speed: null, course: null, heading: null, navigationStatus: null, destination: null, etaUtc: null, lastPositionUtc: null, source: 'datalastic' });
        this.vesselResolveError = 'AIS unavailable. You can still request a product.';
        this.loadSelectedVesselDetails(candidate.imo as string);
      }
    });
  }

  onBillOfLadingLookup(result: BillOfLadingLookupData): void {
    this.bolLookup = result;
    this.bolRoute = undefined;
    this.bolRouteVisible = false;
    this.bolRoutePinned = false;
    this.bolRouteError = '';
    this.datasetRequestMessage = '';
    this.billOfLadingLockedPlaceholders = Array.from(
      { length: Math.max(0, result.fullExport?.providerRecordsNotPreviewed ?? 0) },
      (_, index) => index + 1
    );
  }

  clearBillOfLadingLookup(): void {
    this.bolLookup = undefined;
    this.billOfLadingLockedPlaceholders = [];
    this.bolRoute = undefined;
    this.bolRouteVisible = false;
    this.bolRoutePinned = false;
    this.datasetRequestMessage = '';
  }

  get activeBolPreview() { return this.bolLookup?.previews.find(preview => preview.record) ?? null; }

  get activeTrackedBol(): BillOfLadingTrackedRecord | undefined {
    const number = this.bolLookup?.billOfLadingNumber;
    return number ? this.trackedBols.find(item => item.billOfLadingNumber === number) : undefined;
  }

  loadTrackedBols(): void {
    // Keep the workspace resilient to lightweight API doubles used by embedded/demo hosts.
    if (typeof (this.apiService as any).getTrackedBillOfLadings !== 'function') return;
    this.trackedBolsLoading = true;
    this.apiService.getTrackedBillOfLadings().pipe(takeUntil(this.destroy$), finalize(() => this.trackedBolsLoading = false)).subscribe({
      next: response => { this.trackedBols = response.data ?? []; this.syncTrackedBolRoutes(); this.trackedBols.filter(item => item.mapState.visible && !item.route).forEach(item => this.hydrateTrackedBolRoute(item)); },
      error: () => { this.trackedBolsError = 'Tracked BOLs are temporarily unavailable.'; }
    });
  }

  showBillOfLadingRoute(): void {
    const tracked = this.activeTrackedBol;
    if (tracked) {
      if (tracked.route) {
        this.updateTrackedBolMapState(tracked, { visible: true, pinned: tracked.mapState.pinned });
        return;
      }
      this.bolRouteLoading = true;
      this.bolRouteError = '';
      this.apiService.resolveTrackedBillOfLadingRoute(tracked.id).pipe(takeUntil(this.destroy$), finalize(() => this.bolRouteLoading = false)).subscribe({
        next: response => {
          this.trackedBols = this.trackedBols.map(item => item.id === response.data.id ? response.data : item);
          this.updateTrackedBolMapState(response.data, { visible: true, pinned: response.data.mapState.pinned });
        },
        error: error => {
          this.bolRouteError = error?.status === 404
            ? 'This tracked BOL is no longer available. Reload the workspace and try again.'
            : error?.status === 422
              ? 'The recorded ports could not be resolved.'
              : [429, 502, 503, 504].includes(error?.status)
                ? 'The port route is temporarily unavailable. Please retry.'
                : error?.status === 401 || error?.status === 403
                  ? 'Your session or subscription is no longer valid. Reload and sign in again.'
                  : 'The port route could not be resolved.';
        }
      });
      return;
    }
    const previewToken = this.activeBolPreview?.previewToken;
    if (this.bolRoute) {
      this.bolRouteVisible = true;
      this.syncTrackedBolRoutes();
      if (this.bolLookup) this.vesselMap?.focusBolRoute(-1);
      return;
    }
    if (!previewToken || this.bolRouteLoading) return;
    this.bolRouteLoading = true;
    this.bolRouteError = '';
    this.apiService.resolveBillOfLadingRoute(previewToken).pipe(takeUntil(this.destroy$), finalize(() => this.bolRouteLoading = false)).subscribe({
      next: response => { this.bolRoute = response.data; this.bolRouteVisible = true; this.syncTrackedBolRoutes(); this.vesselMap?.focusBolRoute(-1); },
      error: error => { this.bolRouteError = [422, 429, 502, 503, 504].includes(error?.status) ? 'The port route is temporarily unavailable. Please retry.' : 'The port route could not be resolved.'; }
    });
  }

  hideBillOfLadingRoute(): void {
    const tracked = this.activeTrackedBol;
    if (tracked) {
      this.updateTrackedBolMapState(tracked, { visible: false, pinned: false });
      return;
    }
    this.bolRouteVisible = false;
    this.syncTrackedBolRoutes();
  }

  trackBillOfLading(): void {
    const previewToken = this.activeBolPreview?.previewToken;
    if (!previewToken || this.activeTrackedBol) return;
    this.apiService.createTrackedBillOfLading(previewToken).pipe(
      switchMap(response => this.apiService.resolveTrackedBillOfLadingRoute(response.data.id)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => { this.trackedBols = [response.data, ...this.trackedBols.filter(item => item.id !== response.data.id)]; this.syncTrackedBolRoutes(); },
      error: error => { this.bolRouteError = error?.status === 429 ? 'You can track up to 10 BOLs.' : 'This BOL could not be tracked.'; }
    });
  }

  toggleBillOfLadingPin(): void {
    const tracked = this.activeTrackedBol;
    if (tracked) {
      this.updateTrackedBolMapState(tracked, { visible: true, pinned: !tracked.mapState.pinned });
      return;
    }
    const previewToken = this.activeBolPreview?.previewToken;
    if (!previewToken) return;
    this.apiService.createTrackedBillOfLading(previewToken).pipe(switchMap(response => {
      this.trackedBols = [response.data, ...this.trackedBols.filter(item => item.id !== response.data.id)];
      return this.apiService.resolveTrackedBillOfLadingRoute(response.data.id);
    }), switchMap(response => {
      return this.apiService.updateTrackedBillOfLadingMapState(response.data.id, { visible: true, pinned: true });
    }), takeUntil(this.destroy$)).subscribe({ next: response => { this.trackedBols = this.trackedBols.map(item => item.id === response.data.id ? response.data : item); this.bolRoutePinned = true; this.syncTrackedBolRoutes(); }, error: () => this.bolRouteError = 'This BOL could not be pinned.' });
  }

  updateTrackedBolMapState(tracked: BillOfLadingTrackedRecord, mapState: { visible: boolean; pinned: boolean }): void {
    this.apiService.updateTrackedBillOfLadingMapState(tracked.id, mapState).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => { this.trackedBols = this.trackedBols.map(item => item.id === response.data.id ? response.data : item); this.bolRouteVisible = response.data.mapState.visible; this.bolRoutePinned = response.data.mapState.pinned; this.syncTrackedBolRoutes(); },
      error: error => {
        this.bolRouteError = error?.status === 404
          ? 'This tracked BOL is no longer available. Reload the workspace and try again.'
          : error?.status === 400
            ? 'The BOL map state was rejected by the server.'
            : 'The BOL map state could not be updated. Please retry.';
      }
    });
  }

  removeTrackedBol(tracked: BillOfLadingTrackedRecord, event?: Event): void {
    event?.stopPropagation();
    this.apiService.deleteTrackedBillOfLading(tracked.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.trackedBols = this.trackedBols.filter(item => item.id !== tracked.id); this.syncTrackedBolRoutes(); },
      error: () => this.trackedBolsError = 'The tracked BOL could not be removed.'
    });
  }

  toggleTrackedBolVisibility(tracked: BillOfLadingTrackedRecord, event?: Event): void {
    event?.stopPropagation();
    this.updateTrackedBolMapState(tracked, { visible: !tracked.mapState.visible, pinned: tracked.mapState.pinned && !tracked.mapState.visible ? false : tracked.mapState.pinned });
  }

  requestBroaderBolDataset(): void {
    const previewToken = this.activeBolPreview?.previewToken;
    if (this.datasetRequestLoading) return;
    if (!previewToken) {
      this.datasetRequestMessage = 'Run a fresh BOL lookup before requesting a broader dataset.';
      return;
    }
    this.datasetRequestLoading = true;
    const key = `bol-dataset-${previewToken}`;
    this.apiService.requestBillOfLadingDataset(previewToken, this.activeTrackedBol?.id ?? null, key).pipe(takeUntil(this.destroy$), finalize(() => this.datasetRequestLoading = false)).subscribe({
      next: response => this.datasetRequestMessage = response.status === 'accepted' ? 'Request received. We will follow up with the related dataset.' : 'Request submitted.',
      error: error => {
        this.datasetRequestMessage = error?.status === 400
          ? 'This dataset request needs a fresh BOL lookup.'
          : error?.status === 404
            ? 'This BOL preview has expired. Run the lookup again and retry.'
            : error?.status === 409
              ? 'This dataset request conflicts with an existing request.'
              : 'The dataset request could not be submitted. Please try again.';
      }
    });
  }

  private syncTrackedBolRoutes(): void {
    const current = this.activeTrackedBol;
    const currentRoute = current?.route ?? this.bolRoute;
    if (current && current.route) {
      this.bolRoute = current.route;
      this.bolRouteVisible = current.mapState.visible;
      this.bolRoutePinned = current.mapState.pinned;
    }
    this.persistentBolRoutes = this.trackedBols.filter(item => item.route && item.mapState.visible).map(item => ({
      id: item.id, billOfLadingNumber: item.billOfLadingNumber, color: '#e9c16f', coordinates: item.route!.coordinates, visible: item.mapState.visible, pinned: item.mapState.pinned
    }));
    if (currentRoute && this.bolLookup && this.bolRouteVisible && !this.activeTrackedBol) {
      this.persistentBolRoutes = [...this.persistentBolRoutes, { id: -1, billOfLadingNumber: this.bolLookup.billOfLadingNumber, color: '#e9c16f', coordinates: currentRoute.coordinates, visible: true, pinned: this.bolRoutePinned }];
    }
  }

  private hydrateTrackedBolRoute(tracked: BillOfLadingTrackedRecord): void {
    this.apiService.resolveTrackedBillOfLadingRoute(tracked.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => { this.trackedBols = this.trackedBols.map(item => item.id === response.data.id ? response.data : item); this.syncTrackedBolRoutes(); },
      error: () => undefined
    });
  }

  formatBillOfLadingMeasure(value: number | null | undefined, unit: string | null | undefined): string {
    if (value === null || value === undefined) return 'Not reported';
    return `${value}${unit ? ` ${unit}` : ''}`;
  }

  formatBillOfLadingCountry(country: BillOfLadingCountry): string {
    if (typeof country === 'string') return country || '—';
    return country?.name || country?.code || '—';
  }

  private createEmptyTrackedVessel(): TrackedVessel {
    return {
      callsign: '—',
      destination: '—',
      draught: '—',
      flag: '—',
      mmsi: '—',
      name: 'No vessel selected',
      navigationStatus: '—',
      imo: '—',
      type: '—',
      position: undefined,
      speed: null,
      course: null,
      etaUtc: null,
      lastPositionUtc: null
    };
  }

  private trackedCaseToVessel(vessel: TrackedVesselCase): TrackedVessel {
    return { ...this.createEmptyTrackedVessel(), name: vessel.name, imo: `IMO ${vessel.imo}`, type: vessel.reportType };
  }

  getCurrentPosition(vessel: TrackedVessel): string {
    if (!vessel.position) {
      return '—';
    }

    return `${vessel.position.latitude.toFixed(4)}, ${vessel.position.longitude.toFixed(4)}`;
  }

  displayValue(value: unknown, suffix = ''): string {
    if (value === null || value === undefined || value === '') return 'Not reported';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return `${value}${suffix}`;
  }

  formatPortReference(name: string | null, unlocode: string | null): string {
    return [name, unlocode].filter((value): value is string => Boolean(value)).join(' · ') || 'Not reported';
  }

  formatDetailDate(value: string | null | undefined): string {
    if (!value) return 'Not reported';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(date) + ' UTC';
  }

  detailBlock(name: SelectedVesselBlockName): any {
    return this.selectedVesselDetails?.blocks?.[name];
  }

  detailBlockAvailable(name: SelectedVesselBlockName): boolean {
    return this.detailBlock(name)?.status === 'available';
  }

  hasRouteCoordinates(): boolean {
    const coordinates = this.detailBlock('route')?.data?.coordinates;
    return Array.isArray(coordinates) && coordinates.length > 1
      && coordinates.every((point: number[]) => Number.isFinite(point?.[0]) && Number.isFinite(point?.[1]));
  }

  hasEstimatedPosition(): boolean {
    const estimate = this.detailBlock('estimatedPosition')?.data;
    return Number.isFinite(estimate?.latitude) && Number.isFinite(estimate?.longitude);
  }

  hasWeatherData(): boolean {
    return Boolean(this.detailBlock('weather')?.data);
  }

  retryDetailBlock(name: SelectedVesselBlockName): void {
    const imo = this.activeVessel.imo.replace(/^IMO\s*/i, '');
    if (/^\d{7}$/.test(imo) && !this.retryingDetailBlocks.has(name)) this.loadSelectedVesselDetails(imo, [name]);
  }

  retryAllVesselDetails(): void {
    const imo = this.activeVessel.imo.replace(/^IMO\s*/i, '');
    if (/^\d{7}$/.test(imo)) this.loadSelectedVesselDetails(imo);
  }

  private resetSelectedVesselDetails(): void {
    this.trackingMessage = '';
    this.detailsRequestToken++;
    this.selectedVesselDetails = undefined;
    this.selectedVesselDetailsError = '';
    this.selectedVesselDetailsLoading = false;
    this.weatherLocation = undefined;
    this.retryingDetailBlocks.clear();
  }

  private loadSelectedVesselDetails(imo: string, include: SelectedVesselBlockName[] = SELECTED_VESSEL_BLOCKS): void {
    const token = this.detailsRequestToken;
    const isRetry = include.length === 1 && Boolean(this.selectedVesselDetails);
    if (isRetry) this.retryingDetailBlocks.add(include[0]);
    else this.selectedVesselDetailsLoading = true;
    this.selectedVesselDetailsError = '';
    this.weatherLocation = this.selectedAreaVessel && this.areaBounds
      ? { latitude: (this.areaBounds.south + this.areaBounds.north) / 2, longitude: (this.areaBounds.west + this.areaBounds.east) / 2, label: 'AOI centre' }
      : this.activeVessel.position
        ? { latitude: this.activeVessel.position.latitude, longitude: this.activeVessel.position.longitude, label: this.activeVessel.name }
        : undefined;
    const aoiCenter = this.weatherLocation;
    this.apiService.getSelectedVesselDetails(imo, include, aoiCenter).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        if (token !== this.detailsRequestToken) return;
        this.selectedVesselDetailsLoading = false;
        include.forEach(name => this.retryingDetailBlocks.delete(name));
      })
    ).subscribe({
      next: response => {
        if (token !== this.detailsRequestToken) return;
        const incoming = response?.data;
        if (!incoming?.summary) {
          this.selectedVesselDetailsError = 'Vessel details are temporarily unavailable.';
          return;
        }
        if (isRetry && this.selectedVesselDetails) {
          this.selectedVesselDetails = {
            ...this.selectedVesselDetails,
            summary: incoming.summary,
            blocks: { ...this.selectedVesselDetails.blocks, ...incoming.blocks }
          };
        } else {
          this.selectedVesselDetails = incoming;
        }
        this.applySelectedVesselSummary(incoming.summary);
        if (!this.weatherLocation && this.activeVessel.position && this.selectedVesselDetails.blocks?.weather?.status === 'unavailable') {
          this.retryDetailBlock('weather');
        }
      },
      error: error => {
        if (token !== this.detailsRequestToken) return;
        this.selectedVesselDetailsError = [429, 502, 503].includes(error?.status)
          ? 'Vessel details are temporarily unavailable. Try again.'
          : error?.status === 404 ? 'No vessel details were found for this IMO.'
          : error?.status === 401 ? 'Your session expired. Sign in again to load vessel details.'
          : 'Vessel details could not be loaded.';
      },
    });
  }

  private applySelectedVesselSummary(summary: SelectedVesselDetailsResponse['data']['summary']): void {
    const imo = summary.imo || this.activeVessel.imo.replace(/^IMO\s*/i, '');
    if (imo !== this.activeVessel.imo.replace(/^IMO\s*/i, '')) return;
    const name = summary.name || summary.aisName || this.activeVessel.name;
    const hasPosition = Number.isFinite(summary.latitude) && Number.isFinite(summary.longitude);
    this.activeVessel = {
      ...this.activeVessel,
      name,
      callsign: summary.callsign || 'Not reported',
      destination: summary.destinationPort?.name || summary.originalAisDestination || 'Not reported',
      draught: this.displayValue(summary.currentDraught, ' m'),
      flag: summary.countryName || summary.countryIso || 'Not reported',
      mmsi: summary.mmsi || 'Not reported',
      navigationStatus: summary.navigationStatus || 'Not reported',
      type: summary.typeSpecific || summary.type || 'Not reported',
      speed: summary.speed,
      course: summary.course,
      etaUtc: summary.etaUtc,
      lastPositionUtc: summary.lastPositionUtc,
      position: hasPosition ? {
        longitude: summary.longitude as number,
        latitude: summary.latitude as number,
        heading: summary.heading ?? undefined,
        name,
        imo: `IMO ${imo}`
      } : undefined
    };
    this.mapPositions = this.mergeMapPosition(this.activeVessel.position);
  }

  getSpeedCourse(vessel: TrackedVessel): string {
    const speed = vessel.speed === null ? '—' : `${vessel.speed} kn`;
    const course = vessel.course === null ? '—' : `${vessel.course}°`;
    const heading = vessel.position?.heading === undefined ? '—' : `Hdg ${vessel.position.heading}°`;
    return vessel.speed === null && vessel.course === null
      ? (heading === '—' ? 'Not reported' : heading)
      : `${speed} · ${course} · ${heading}`;
  }

  private toTrackedVessel(candidate: VesselSearchCandidate, live: VesselLiveObservation): TrackedVessel {
    const imo = live.imo || candidate.imo || '—';
    const name = live.name || candidate.name || 'Unnamed vessel';
    const hasPosition = Number.isFinite(live.latitude) && Number.isFinite(live.longitude);

    return {
      callsign: '—',
      destination: live.destination ?? '—',
      draught: '—',
      flag: candidate.countryIso ?? '—',
      mmsi: live.mmsi ?? candidate.mmsi ?? '—',
      name,
      navigationStatus: live.navigationStatus ?? '—',
      imo: `IMO ${imo}`,
      type: candidate.type ?? '—',
      position: hasPosition ? {
        longitude: live.longitude as number,
        latitude: live.latitude as number,
        heading: live.heading ?? undefined,
        name,
        imo: `IMO ${imo}`
      } : undefined,
      speed: live.speed,
      course: live.course,
      etaUtc: live.etaUtc,
      lastPositionUtc: live.lastPositionUtc
    };
  }

  private mergeMapPosition(position: VesselMapPosition | undefined): VesselMapPosition[] {
    const tracked = (this.mapPositions ?? []).filter(candidate => candidate.imo !== position?.imo);
    return position ? [...tracked, position] : tracked;
  }

  private removeMapPosition(imo: string): VesselMapPosition[] {
    return (this.mapPositions ?? []).filter(candidate => candidate.imo !== `IMO ${imo}` && candidate.imo !== imo);
  }

  selectReport(report: string): void {
    this.selectedReport = report;
  }

  openVesselSearch(): void {
    this.currentStep = 'search';
    this.showSearchForm = false;
    document.querySelector<HTMLInputElement>('input[name="vesselQuery"]')?.focus();
    this.scrollToTop();
  }

  openWorkspace(): void {
    this.currentStep = 'search';
    this.showSearchForm = false;
    this.scrollToTop();
  }

  openReportSelection(report: string): void {
    this.selectReport(report);
    this.directProductSelection = false;
    const aliases: Record<string, string> = { 'Vessel UCB': 'R8', 'AVI (Advanced Vessel Intelligence)': 'R12', 'Deep Dive Intelligence': 'R10', 'Dark Ship Identification': 'R11' };
    const selectableProducts = this.hasSelectedVessel ? this.availableVesselProducts : this.availableProductsWithoutSelectedVessel;
    const selectedPackage = selectableProducts.find(pack => pack.name === report || pack.code === report || pack.code === aliases[report]);
    if (!selectedPackage) { this.catalogError = 'This product is not available for vessels.'; return; }
    const currentImo = this.activeVessel.imo.replace(/^IMO\s*/i, '');
    if (selectedPackage.quoteOnly) {
      this.dialog.open(MaritimeRequestDialogComponent, {
        data: { kind: 'product_request', products: [selectedPackage], query: this.hasSelectedVessel ? currentImo : '', vesselContext: this.hasSelectedVessel ? this.getCheckoutVesselContext() : undefined },
        panelClass: 'tevuna-maritime-request',
        backdropClass: 'tevuna-maritime-request-backdrop'
      });
      return;
    }
    if (selectedPackage.code === 'R8' && !/^\d{7}$/.test(currentImo)) {
      this.catalogError = 'A valid IMO is required to start a Vessel UCB report.';
      return;
    }
    this.selectedEntities.vessels = [{
      name: this.activeVessel.name,
      imoNumber: this.activeVessel.imo.replace('IMO ', ''),
      type: 'VESSEL'
    }];
    this.manualPaidRequest = selectedPackage.manual === true;
    this.selectedPackages = [selectedPackage];
    this.directProductSelection = true;
    this.goToStep('checkout');
  }

  get availableVesselProducts(): Package[] {
    return this.packages.filter(product => product.available !== false && !product.requiresShipment && product.supportedEntityTypes?.includes('VESSEL'));
  }

  get hasSelectedVessel(): boolean {
    return this.activeVessel.name !== 'No vessel selected';
  }

  get availableProductsWithoutSelectedVessel(): Package[] {
    return this.availableVesselProducts.filter(product => product.quoteOnly);
  }

  getVesselProductPriceLabel(product: Package): string {
    if (product.quoteOnly) return 'Quote on request';
    if (product.price === null || product.price === undefined) return '';
    return ['R8', 'R12'].includes(product.code) ? `$${product.price.toLocaleString()} / vessel` : `$${product.price.toLocaleString()}`;
  }

  private loadProductCatalog(): void {
    if (typeof (this.apiService as any).getPackages !== 'function') return;
    this.catalogLoading = true;
    this.apiService.getPackages().pipe(takeUntil(this.destroy$)).subscribe({
      next: products => { this.packages = (products || []).map((product: any) => ({ ...product, features: product.features || [], ubo_add_on: product.ubo_add_on ?? false, supportedEntityTypes: product.supportedEntityTypes || ['VESSEL'] })); this.catalogLoading = false; },
      error: () => { this.catalogLoading = false; this.catalogError = 'Product catalog is temporarily unavailable.'; }
    });
  }

  onVesselSearchNoMatch(event: { query: string; imo: string | null }): void {
    this.resetSelectedVesselDetails();
    this.selectedAreaVessel = undefined;
    this.unresolvedVessel = { name: event.query, imo: event.imo };
    this.activeVessel = this.createEmptyTrackedVessel();
    this.activeVessel.name = event.query;
    this.activeVessel.imo = event.imo ? `IMO ${event.imo}` : 'IMO —';
    this.unresolvedVesselContext = { query: event.query, name: event.query, imo: event.imo };
    this.vesselResolveError = '';
    if (event.imo && /^\d{7}$/.test(event.imo)) this.loadSelectedVesselDetails(event.imo);
  }

  private isValidStep(step: string): step is 'search' | 'packages' | 'checkout' {
    return ['search', 'packages', 'checkout'].includes(step);
  }

  private validateStepAccess(): void {
    // Redirect to search if trying to access later steps without required data
    if (this.currentStep === 'packages' && this.getTotalSelectedEntities() === 0) {
      this.navigateToStep('search', true);
      return;
    }
    
    if (this.currentStep === 'checkout' && 
        (this.getTotalSelectedEntities() === 0 || this.selectedPackages.length === 0)) {
      this.navigateToStep('search', true);
      return;
    }
  }

  private navigateToStep(step: 'search' | 'packages' | 'checkout', replaceUrl = false): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      queryParamsHandling: 'merge',
      ...(replaceUrl ? { replaceUrl: true } : {})
    });
  }

  private scrollToTop(): void {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  goToStep(step: 'search' | 'packages' | 'checkout'): void {
    this.navigateToStep(step);
    this.scrollToTop();
  }

  goToNextStep(): void {
    switch (this.currentStep) {
      case 'search':
        if (this.getTotalSelectedEntities() > 0) {
          this.goToStep('packages');
        }
        break;
      case 'packages':
        if (this.selectedPackages.length > 0) {
          this.goToStep('checkout');
        }
        break;
    }
  }

  goToPreviousStep(): void {
    switch (this.currentStep) {
      case 'packages':
        this.goToStep('search');
        break;
      case 'checkout':
        this.goToStep('packages');
        break;
    }
  }

  onStepIndicatorClick(step: 'search' | 'packages' | 'checkout'): void {
    // Validate that the step is accessible before navigating
    const canAccessStep = this.canAccessStep(step);
    if (canAccessStep) {
      this.goToStep(step);
    }
  }

  private canAccessStep(step: 'search' | 'packages' | 'checkout'): boolean {
    switch (step) {
      case 'search':
        return true; // Search is always accessible
      case 'packages':
        return this.getTotalSelectedEntities() > 0; // Can only access packages if search is completed
      case 'checkout':
        return this.getTotalSelectedEntities() > 0 && this.selectedPackages.length > 0; // Can only access checkout if both previous steps are completed
      default:
        return false;
    }
  }

  onEntitySelectionChanged(entities: any[], type: 'company' | 'individual' | 'vessel'): void {
    if (type === 'company') {
      this.selectedEntities.companies = entities;
    } else if (type === 'individual') {
      this.selectedEntities.individuals = entities;
    } else {
      this.selectedEntities.vessels = entities;
    }
  }

  onSearchResultsReceived(results: any, type: 'company' | 'individual' | 'vessel'): void {
    if (type === 'company') {
      this.searchResults.companies = results.companies;
    } else if (type === 'individual') {
      this.searchResults.individuals = results.individuals;
    } else {
      this.searchResults.vessels = results.vessels;
    }
    this.currentEntityType = type;
  }

  getTotalSelectedEntities(): number {
    return this.selectedEntities.companies.length + this.selectedEntities.individuals.length + this.selectedEntities.vessels.length;
  }

  getSelectedEntityTypes(): ('COMPANY' | 'INDIVIDUAL' | 'VESSEL')[] {
    const types: ('COMPANY' | 'INDIVIDUAL' | 'VESSEL')[] = [];
    if (this.selectedEntities.companies.length > 0) {
      types.push('COMPANY');
    }
    if (this.selectedEntities.individuals.length > 0) {
      types.push('INDIVIDUAL');
    }
    if (this.selectedEntities.vessels.length > 0) {
      types.push('VESSEL');
    }
    return types;
  }

  getSelectedEntitiesForCheckout(): any[] {
    const formattedCompanies = this.selectedEntities.companies.map(company => ({
      target: {
        companyName: company.name,
        country: company.country,
        address: company.address?.simpleValue,
      },
      ...company,
      country: company.country,
      code: company.safeNo,
      type: 'COMPANY'
    }));
    
    const formattedIndividuals = this.selectedEntities.individuals.map(individual => ({
      target: {
        fullName: individual.firstName + ' ' + individual.lastName,
        country: individual.country,
        address: individual.address?.simpleValue
      },
      ...individual,
      country: individual.country,
      code: individual.peopleId,
      name: individual.firstName + ' ' + individual.lastName,
      type: 'INDIVIDUAL'
    }));

    const formattedVessels = this.selectedEntities.vessels.map(vessel => ({
      target: {
        name: vessel.name,
        imoNumber: vessel.imoNumber
      },
      ...vessel,
      country: 'IT', // Fixed country for vessels as per documentation
      code: vessel.imoNumber,
      name: vessel.name,
      type: 'VESSEL'
    }));
    
    return [...formattedCompanies, ...formattedIndividuals, ...formattedVessels];
  }

  getCheckoutVesselContext(): Record<string, unknown> {
    const selected = this.selectedEntities.vessels[0] || {};
    const activeImo = String(selected.imoNumber ?? this.activeVessel.imo ?? '').replace(/^IMO\s*/i, '').trim();
    const name = selected.name || this.activeVessel.name || this.unresolvedVessel?.name || null;
    return {
      ...this.unresolvedVesselContext,
      ...this.selectedVesselDetails,
      name,
      imo: /^\d{7}$/.test(activeImo) ? activeImo : (this.unresolvedVessel?.imo || null),
      imoNumber: /^\d{7}$/.test(activeImo) ? activeImo : (this.unresolvedVessel?.imo || null),
      type: 'VESSEL'
    };
  }

  onPackageToggled(pack: Package): void {
    const packageIndex = this.selectedPackages.findIndex(p => p.code === pack.code);
    if (packageIndex !== -1) {
      this.selectedPackages.splice(packageIndex, 1);
    } else {
      this.selectedPackages.push(pack);
    }
  }

  onPackagesSelected(): void {
    this.goToNextStep();
  }

  onUboToggled(selected: boolean): void {
    this.uboSelected = selected;
  }

  // Step description methods
  getStepTitle(): string {
    switch (this.currentStep) {
      case 'search':
        return '1. Start your search.';
      case 'packages':
        return '2. Choose a report.';
      case 'checkout':
        return '3. Confirm your report.';
      default:
        return '';
    }
  }

  getStepDescription(): string {
    switch (this.currentStep) {
      case 'search':
        return 'Search for a known company or individual to start your search';
      case 'packages':
        return 'Choose one or multiple reports';
      case 'checkout':
        return 'Review and Confirm';
      default:
        return '';
    }
  }

  // UBO helper methods
  hasUboAvailable(): boolean {
    return this.selectedPackages.some(pkg => pkg.ubo_add_on !== false);
  }

  getUboSupportedPackages(): Package[] {
    return this.selectedPackages.filter(pkg => pkg.ubo_add_on !== false);
  }

  get uboSupportedPackageNames(): string {
    return this.getUboSupportedPackages().map(p => p.name).join(', ');
  }

  // Legacy methods for backward compatibility
  getDiligence(data: any) {
    this.dialog.open(ConfirmReportComponent, {
      data: Array.isArray(data) ? data : [data],
      width: '80%', 
      height: '80%', 
      maxWidth: '100vw',
      maxHeight: '100vh'
    });
  }

  openDiligenceById(id: string) {
    this.router.navigate(['/dueDiligence', id]);
  }

  canGoBack(): boolean {
    // Can go back from packages or checkout steps
    return this.currentStep === 'packages' || this.currentStep === 'checkout';
  }

  canContinue(): boolean {
    switch (this.currentStep) {
      case 'packages':
        return this.selectedPackages.length > 0;
      case 'search':
        return this.getTotalSelectedEntities() > 0;
      default:
        return false;
    }
  }

  getBackButtonText(): string {
    switch (this.currentStep) {
      case 'packages':
        return 'Back to Search';
      case 'checkout':
        return 'Back to Packages';
      default:
        return 'Back';
    }
  }

  getContinueButtonText(): string {
    switch (this.currentStep) {
      case 'search':
        return `Continue`;
      case 'packages':
        return 'Confirm';
      default:
        return 'Continue';
    }
  }

  toggleToSearchForm(entityType?: 'Companies' | 'Individuals' | 'Vessels'): void {
    this.initialSearchFormType = entityType || 'Companies';
    this.showSearchForm = true;
  }

  returnToSearch(): void {
    this.showSearchForm = false;
  }

  onSearchFormSubmit(formData: any): void {
      if (formData.type === 'companies') {
        const companyEntities = formData.entities.map((entity: any) => ({
          name: entity.companyName,
          country: entity.country,
          address: { simpleValue: entity.address || '' },
          safeNo: entity.id || 'manual-' + Date.now(),
          type: 'COMPANY',
          isManualEntry: true,
          companySize: entity.companySize,
          market: entity.market
        }));
        this.selectedEntities.companies = companyEntities;
      } else if (formData.type === 'individuals') {
        const individualEntities = formData.entities.map((entity: any) => ({
          firstName: entity.fullName.split(' ')[0] || entity.fullName,
          lastName: entity.fullName.split(' ').slice(1).join(' ') || '',
          country: entity.country,
          address: { simpleValue: entity.address || '' },
          peopleId: entity.id || 'manual-' + Date.now(),
          type: 'INDIVIDUAL',
          isManualEntry: true
        }));
        this.selectedEntities.individuals = individualEntities;
      } else if (formData.type === 'vessels') {
        const vesselEntities = formData.entities.map((entity: any) => ({
          name: entity.name,
          imoNumber: entity.imoNumber,
          id: entity.id || 'manual-' + Date.now(),
          type: 'VESSEL',
          isManualEntry: true
        }));
        this.selectedEntities.vessels = vesselEntities;
      }
    this.goToStep('packages');
  }
}
